import React from "react";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Clock, CheckCircle, AlertCircle, FileText, Gauge, ArrowLeft, TrendingUp, Users, Award, Star } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import Image from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import UscoreAdvertisement from "@/components/home/UscoreAdvertisement";
import { KayttokohteetCard } from "@/components/home";
import { motion } from "framer-motion";

const KulutusluottoPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/lainat/kulutusluotto");

  const loanConsiderationDetails = [
    {
      icon: <BarChart3 className="h-6 w-6 text-blue-500" />,
      title: "Kokonaiskulut",
      description: "Huomioi koron lisäksi myös avausmaksut, käsittelykulut ja muut mahdolliset lisämaksut."
    },
    {
      icon: <Clock className="h-6 w-6 text-green-500" />,
      title: "Laina-aika",
      description: "Pidempi laina-aika pienentää kuukausierää, mutta nostaa kokonaiskustannuksia."
    },
    {
      icon: <AlertCircle className="h-6 w-6 text-purple-500" />,
      title: "Todellinen vuosikorko",
      description: "Todellinen vuosikorko sisältää kaikki lainan kustannukset ja on paras mittari vertailuun."
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-orange-500" />,
      title: "Joustavuus",
      description: "Tarkista, onko lainassa mahdollisuus maksuvapaisiin kuukausiin tai ennenaikaiseen takaisinmaksuun."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainat/kulutusluotto"
        schema={seoData.schema}
      />
      
      <div className="container mx-auto px-4 py-4">
        <Link 
          to="/lainatuotteet" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Takaisin lainatuotteisiin
        </Link>
      </div>
      
      <div className="relative h-[50vh] md:h-[55vh] min-h-[400px] max-h-[480px]">
        <Image 
          src="/lovable-uploads/a87b1dc3-2720-4507-ac4b-0ae9db69c6b5.png"
          alt="Nuori nainen kannettavan tietokoneen äärellä kortin kanssa" 
          className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
          fallbackSrc="/placeholder.svg"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/20">
          <div className="container mx-auto px-4 md:px-6 h-full flex items-center">
            <div className="max-w-lg text-white space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-4xl font-bold leading-tight">
                Kilpailuta <span className="text-blue-400">kulutusluotto</span> helposti
              </h1>
              <p className="text-base md:text-lg leading-relaxed">
                Täytä yksi hakemus ja vertaile eri pankkien tarjouksia. Säästä jopa satoja euroja.
              </p>
              <div className="pt-2">
                <LainaCta 
                  text={isMobile ? "Hae lainaa" : "Hae kulutusluottoa nyt"} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300"
                  variant="primary"
                  size={isMobile ? "md" : "lg"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 pt-10">
        {/* Enhanced benefits bar with social proof */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-4 mb-10 md:mb-12 relative z-10"
        >
          {[
            { text: isMobile ? "Päätös 24h" : "Päätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Korot alk. 4,9%" : "Edulliset korot alkaen 4,9%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Ei vakuuksia" : "Ei vaadi vakuuksia", icon: <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "1-15v maksuaika" : "Maksuaika 1-15 vuotta", icon: <Gauge className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
              className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* New social proof section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center mb-8 md:mb-12"
        >
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-green-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-100 rounded-full -mt-10 -mr-10 opacity-50"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-600">4.8/5</span>
              </div>
              <p className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                Yli <span className="text-green-600 font-bold">50 000</span> tyytyväistä asiakasta
              </p>
              <p className="text-sm md:text-base text-gray-600">
                "Säästin 340€ vuodessa kilpailuttamalla kulutusluottoni Lainasampo.fi:n kautta"
              </p>
              <div className="flex items-center justify-center mt-3">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-bold">
                    +50k
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-10">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6 -mt-6 md:mb-16">
              <div>
                <div className="relative rounded-xl overflow-hidden mb-3 group transform hover:scale-[1.01] transition-all duration-300">
                  <div className="w-full h-[300px] md:h-[400px] bg-blue-100">
                    <img
                      src="/lovable-uploads/fc05f257-7187-4a2b-92be-872aec5c7c3e.png"
                      alt="Pariskunta käyttää tietokonetta"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 flex flex-col justify-end">
                    <h3 className="text-white text-lg md:text-xl font-bold mb-2">Monipuoliset käyttökohteet</h3>
                    <ul className="space-y-1 text-white text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Kodin remontit ja hankinnat</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Lomamatkat ja harrastukset</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Yllättävät menot</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <div className="relative rounded-xl overflow-hidden mb-3 group transform hover:scale-[1.01] transition-all duration-300">
                  <div className="w-full h-[300px] md:h-[400px] bg-blue-100">
                    <img
                      src="/lovable-uploads/9813b8b5-ba28-4727-b82e-f7db9186d71f.png"
                      alt="Seniorimies käyttää puhelinta ja luottokorttia"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 flex flex-col justify-end">
                    <h3 className="text-white text-lg md:text-xl font-bold mb-2">Kilpailuta lainatarjoukset</h3>
                    <ul className="space-y-1 text-white text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Säästä satoja euroja</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Helppo online-hakemus</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Nopea päätös</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="text-gray-800">
                  Mitä huomioida kulutusluotossa?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-blue-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-blue-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center mb-2 md:mb-3">
                      <BarChart3 className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-blue-800 mb-1 md:mb-2 text-sm md:text-base">Kokonaiskulut</h4>
                    <p className="text-xs md:text-sm text-gray-700">Huomioi koron lisäksi myös avausmaksut, käsittelykulut ja muut mahdolliset lisämaksut.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-green-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-green-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-green-500 to-green-700 flex items-center justify-center mb-2 md:mb-3">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-green-800 mb-1 md:mb-2 text-sm md:text-base">Laina-aika</h4>
                    <p className="text-xs md:text-sm text-gray-700">Pidempi laina-aika pienentää kuukausierää, mutta nostaa kokonaiskustannuksia.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-purple-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-purple-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-purple-500 to-purple-700 flex items-center justify-center mb-2 md:mb-3">
                      <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-purple-800 mb-1 md:mb-2 text-sm md:text-base">Todellinen vuosikorko</h4>
                    <p className="text-xs md:text-sm text-gray-700">Todellinen vuosikorko sisältää kaikki lainan kustannukset ja on paras mittari vertailuun.</p>
                  </div>
                </div>
                
                <div className="relative overflow-hidden group">
                  <div className="absolute inset-0 bg-orange-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"></div>
                  <div className="p-3 md:p-5 bg-white border border-orange-100 rounded-xl relative group-hover:shadow-md transition-all">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-700 flex items-center justify-center mb-2 md:mb-3">
                      <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <h4 className="font-bold text-orange-800 mb-1 md:mb-2 text-sm md:text-base">Joustavuus</h4>
                    <p className="text-xs md:text-sm text-gray-700">Tarkista, onko lainassa mahdollisuus maksuvapaisiin kuukausiin tai ennenaikaiseen takaisinmaksuun.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="text-gray-800">
                  Miksi valita kulutusluotto?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    icon: <FileText className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Joustava rahoitus",
                    description: "Käytä vapaasti haluamaasi tarkoitukseen, olipa kyse remontista, matkasta tai yllättävistä menoista."
                  },
                  {
                    icon: <ShieldCheck className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Ilman vakuuksia",
                    description: "Saat lainan ilman takaajia tai vakuuksia, mikä tekee hakemisesta helppoa ja nopeaa."
                  },
                  {
                    icon: <BarChart3 className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Kilpailutettu korko",
                    description: "Vertaamalla saat edullisimman koron ja säästät satoja euroja lainan kokonaiskustannuksissa."
                  }
                ].map((feature, index) => (
                  <div key={index} className="relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl transform group-hover:scale-105 transition-transform duration-300"></div>
                    <Card className="h-full bg-transparent border-0 relative z-10 bg-white/95 backdrop-blur-sm group-hover:bg-white/100 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center mb-2 md:mb-3">
                          {feature.icon}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-blue-800 mb-1 md:mb-2">{feature.title}</h3>
                        <p className="text-xs md:text-sm text-gray-700">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </section>
            
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-600">
                  Hyödyllisiä artikkeleita
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 mt-4 md:mt-6">
                {[
                  {
                    title: "Kulutusluotto vai joustoluotto?",
                    url: "/artikkelit/kulutusluottovertailu-kulutusluotto-vs-joustoluotto",
                    description: "Mikä on näiden kahden luottotyypin ero"
                  },
                  {
                    title: "Lainan kilpailuttaminen",
                    url: "/artikkelit/lainan-kilpailuttaminen-saasta-satoja-euroja",
                    description: "Näin säästät satoja euroja lainassasi"
                  },
                  {
                    title: "Vastuullinen lainaaminen",
                    url: "/artikkelit/vastuullinen-lainaaminen-nain-onnistut-takaisinmaksussa",
                    description: "Näin onnistut lainan takaisinmaksussa"
                  }
                ].map((article, i) => (
                  <Link to={article.url} key={i} className="group">
                    <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 h-full transform group-hover:-translate-y-1 group-hover:shadow-lg transition-all duration-300">
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                        <p className="text-gray-700 mb-4">{article.description}</p>
                        <div className="flex items-center text-blue-600 font-medium">
                          Lue lisää <ArrowRight className="ml-2 h-4 w-4 group-hover:ml-3 transition-all" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
          
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)" />
                    <defs>
                      <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                        <circle cx="5" cy="5" r="1" fill="white" />
                      </pattern>
                    </defs>
                  </svg>
                </div>
                <div className="relative p-6 text-white">
                  <h2 className="text-2xl font-bold mb-4">Kilpailuta kulutusluottosi nyt!</h2>
                  <p className="mb-6">
                    Lainan kilpailutuksessa voit säästää jopa 30% lainasi kokonaiskustannuksissa!
                  </p>
                  <LainaCta 
                    variant="primary" 
                    size="lg" 
                    text="Hae kulutusluottoa" 
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                  />
                </div>
              </div>
              
              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Kulutusluotto lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[
                      { label: "Laina-aika:", value: "1-15 vuotta" },
                      { label: "Lainasumma:", value: "2 000€ - 25 000€" },
                      { label: "Korko alkaen:", value: "4,9%" },
                      { label: "Vakuus:", value: "Ei tarvita" },
                      { label: "Käsittelyaika:", value: "1-2 arkipäivää" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 30% lainasi kokonaiskustannuksissa!
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-800">Katso myös nämä rahoitusvaihtoehdot</h3>
                <div className="space-y-3">
                  {[
                    { name: "Joustoluotto", url: "/lainat/joustoluotto" },
                    { name: "Autolaina", url: "/lainat/autolaina" },
                    { name: "Remonttilaina", url: "/lainat/remonttilaina" },
                    { name: "Yhdistelylaina", url: "/lainat/yhdistelylaina" }
                  ].map((option, i) => (
                    <Link
                      key={i}
                      to={option.url}
                      className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 font-medium transition-colors group"
                    >
                      <span>{option.name}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* New Trust & Security Section */}
        <motion.section 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-16"
        >
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-6 md:p-8 border border-gray-100">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-800">
              Miksi Lainasampo.fi on turvallinen valinta?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Rekisteröity välittäjä</h3>
                <p className="text-gray-600 text-sm">
                  Toimimme virallisena luotonvälittäjänä ja noudatamme kaikkia GDPR-vaatimuksia
                </p>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Yli 50 000 asiakasta</h3>
                <p className="text-gray-600 text-sm">
                  Olemme auttaneet tuhansia suomalaisia löytämään edullisimman lainan
                </p>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Maksuton palvelu</h3>
                <p className="text-gray-600 text-sm">
                  Kilpailutuspalvelumme on täysin maksuton - saat säästöt itsellesi
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* UScore Advertisement Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 md:mb-16"
        >
          <UscoreAdvertisement />
        </motion.section>
        
        <section className="mb-8 md:mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Kulutusluoton yleisimmät käyttökohteet
            </span>
          </motion.h2>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8"
          >
            <KayttokohteetCard
              title="Kodin hankinnat"
              description="Uusi kodinkone, huonekalut tai muu sisustus"
              bgColor="bg-blue-100"
              imagePath="/lovable-uploads/98c98f80-8bd4-4bd1-acf2-263736d0bc9a.png"
            />
            <KayttokohteetCard
              title="Lomamatkat"
              description="Unelmien matka kotimaassa tai ulkomailla"
              bgColor="bg-green-100"
              imagePath="/lovable-uploads/6c25c292-d9e1-4432-8353-217ee56ada10.png"
            />
            <KayttokohteetCard
              title="Ostosten maksaminen"
              description="Vaivaton maksaminen korttiveloituksella"
              bgColor="bg-amber-100"
              imagePath="/lovable-uploads/796dca8f-b0a6-4e80-b2ce-6129afa71980.png"
            />
          </motion.div>
        </section>
        
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,62,39.5C52.9,54.5,38.1,66.4,21.7,69.6C5.4,72.7,-12.4,67.1,-29.9,58.5C-47.4,49.9,-64.5,38.3,-73.2,21.3C-81.9,4.4,-82.3,-17.8,-72.5,-32.5C-62.7,-47.2,-42.8,-54.3,-26.2,-63.1C-9.6,-71.9,3.8,-82.4,18.2,-79.9C32.6,-77.5,48.9,-62.1,47.1,-57.8Z" transform="translate(100 100)" />
            </svg>
          </div>
          <div className="px-6 py-8 md:px-10 md:py-12 relative z-10">
            <div className="max-w-5xl mx-auto text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Ota ensimmäinen askel kohti edullisempaa lainaa</h2>
              <p className="mb-6 text-blue-100 max-w-3xl text-base md:text-lg">
                Kilpailuta kulutusluottosi nyt ja säästä jopa satoja euroja. Täytä lainahakemus kerran ja saat useita lainatarjouksia eri pankeilta!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <LainaCta 
                  variant="primary" 
                  size="lg" 
                  text="Hae kulutusluottoa nyt" 
                  className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                />
                <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700/30 hover:bg-blue-700/50 border border-blue-400/30 backdrop-blur-sm text-white font-medium transition-all duration-300">
                  Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default KulutusluottoPage;
